import type { CellContext } from '@tanstack/react-table'
import { ArrowLeftRightIcon } from 'lucide-react'
import { useState } from 'react'

import { useToast } from '@/hooks'
import {
  type Check,
  type CheckDirection,
  type Transaction,
  useProjectMutations,
} from '@/modules/projects'
import { cn } from '@/utils'

const BADGE =
  'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border'

const RESOLVED: Record<
  string,
  { label: string; style: string; opposite: CheckDirection }
> = {
  deposit: {
    label: 'In',
    style: 'text-emerald-700 border-emerald-200 bg-emerald-50',
    opposite: 'payment',
  },
  payment: {
    label: 'Out',
    style: 'text-red-700 border-red-200 bg-red-50',
    opposite: 'deposit',
  },
}

// A resolved check flips In <-> Out in one click; lukita re-signs the amount. An unresolved check
// is set in the check review workspace instead.
export const DirectionCell = (cell: CellContext<Transaction, unknown>) => {
  const check = cell.row.original as unknown as Check
  const { updateCheck } = useProjectMutations()
  const { errorToast } = useToast()
  const [saving, setSaving] = useState(false)

  if (check.needsReview) {
    return (
      <span
        className={cn(BADGE, 'text-amber-700 border-amber-200 bg-amber-50')}
      >
        Needs review
      </span>
    )
  }

  const resolved = RESOLVED[check.direction ?? '']
  if (!resolved) {
    return (
      <span aria-hidden="true" className="text-muted-foreground select-none">
        —
      </span>
    )
  }

  const oppositeLabel = RESOLVED[resolved.opposite].label
  return (
    <button
      type="button"
      title={`Change to ${oppositeLabel}`}
      aria-label={`Direction ${resolved.label}. Change to ${oppositeLabel}`}
      onClick={(event: React.MouseEvent) => {
        event.stopPropagation()
        setSaving(true)
        updateCheck(check.id, { direction: resolved.opposite })
          .catch((error) =>
            errorToast('Failed to change check direction', error),
          )
          .finally(() => setSaving(false))
      }}
      disabled={saving}
      className={cn(
        BADGE,
        resolved.style,
        'cursor-pointer transition-opacity hover:opacity-80 disabled:cursor-wait disabled:opacity-50',
      )}
    >
      {resolved.label}
      <ArrowLeftRightIcon className="size-3" />
    </button>
  )
}
